import { SSMT } from '../../../constants/qnames';

/**
 * Shared business rules for Role 5 (Statement by Directors).
 *
 * Both role5-120100-statement-by-directors.component.ts AND the pure
 * role5StatementByDirectorsValidator import from this file so the dynamic
 * director-visibility, identification-type pattern, and
 * "requiredIfAllVisibleDirectorsMatch" logic stay DRY.
 *
 * Modify rules HERE only — anywhere else risks the parity-check harness
 * throwing in dev because the two paths disagree.
 *
 * This mirrors role4-directors-report.rules.ts with one extra concept:
 * three "Other person primarily responsible for financial management" fields
 * that live outside the per-director set and become required only when every
 * visible director reports "Not primarily responsible..." (see
 * evaluateRequiredIfAllVisibleDirectorsMatch in the component).
 */

export const ROLE5_NUMBER_OF_DIRECTORS_KEY =
  SSMT.NumberOfDirectorsSigningStatementByDirectors;

/**
 * Sentinel for "no director count is selected" — ZERO directors are visible.
 * THE single definition, imported by the component, the pure validator and the
 * submit projector. Mirrors ROLE4_NO_DIRECTOR_COUNT_SELECTED; see that constant
 * for why the old `= 2` fallback was removed (2026-07-29).
 */
export const ROLE5_NO_DIRECTOR_COUNT_SELECTED = 0;

/**
 * Identification types that trigger the 12-digit pattern requirement on any
 * IdentificationNumber field (per-director OR the "other person" one).
 * Other types (Passport, etc.) remove the pattern; the director cases keep
 * the required check when visible, and the "other person" case keeps
 * required only when the requiredIfAllVisibleDirectorsMatch condition is met.
 */
export const ROLE5_IDENTIFICATION_TYPES_REQUIRING_PATTERN: ReadonlyArray<string> = [
  'MyKad',
  'MyPR',
  'MyKAS',
];

/** Pattern applied to an IdentificationNumber when type is in the list above. */
export const ROLE5_IDENTIFICATION_PATTERN = /^[0-9]{12}$/;

/**
 * Resolve the effective number of visible directors from raw form data.
 *
 * An absent, empty or unparseable count resolves to
 * ROLE5_NO_DIRECTOR_COUNT_SELECTED (0) — identical to the component's
 * `resolveDirectorCount`, which imports this same function. The earlier
 * "resolve to 2" divergence (2026-07-28) was **removed 2026-07-29**: the submit
 * projector shares this resolver, and a `2` there let directors 1–2 ship in the
 * payload while nothing was rendered on screen. See
 * ROLE4_NO_DIRECTOR_COUNT_SELECTED for the full rationale and the accepted
 * headless consequence.
 */
export function getRole5VisibleDirectorCount(
  sheetData: Record<string, any>
): number {
  const raw = sheetData[ROLE5_NUMBER_OF_DIRECTORS_KEY];
  if (raw === null || raw === undefined || raw === '') {
    return ROLE5_NO_DIRECTOR_COUNT_SELECTED;
  }
  const parsed = parseInt(String(raw), 10);
  return Number.isFinite(parsed) ? parsed : ROLE5_NO_DIRECTOR_COUNT_SELECTED;
}

/**
 * Case-insensitive membership check used by both the component's
 * shouldUseNumericFilter and the pure validator to decide whether the
 * 12-digit pattern applies to a given IdentificationNumber field.
 */
export function role5TypeRequiresPattern(
  typeValue: unknown,
  allowedValues: ReadonlyArray<string> = ROLE5_IDENTIFICATION_TYPES_REQUIRING_PATTERN
): boolean {
  if (typeof typeValue !== 'string' || typeValue.length === 0) return false;
  const normalized = typeValue.toLowerCase();
  return allowedValues.some((allowed) => allowed.toLowerCase() === normalized);
}

/**
 * Evaluate the schema-driven requiredIfAllVisibleDirectorsMatch condition
 * from a flat sheetData snapshot. Mirrors the component's
 * evaluateRequiredIfAllVisibleDirectorsMatch: for i in [0, visibleCount),
 * every listed director field must equal `config.equals`. Empty or
 * mismatched values short-circuit to false.
 */
export function evaluateRole5RequiredIfAllVisibleDirectorsMatch(
  sheetData: Record<string, any>,
  config: {
    directorCountField: string;
    directorFields: ReadonlyArray<string>;
    equals: string;
  }
): boolean {
  const rawCount = sheetData[config.directorCountField];
  const count =
    rawCount === null || rawCount === undefined || rawCount === ''
      ? ROLE5_NO_DIRECTOR_COUNT_SELECTED
      : (() => {
          const parsed = parseInt(String(rawCount), 10);
          return Number.isFinite(parsed)
            ? parsed
            : ROLE5_NO_DIRECTOR_COUNT_SELECTED;
        })();

  // With ZERO visible directors the loop below never runs and "all visible
  // directors match" would be VACUOUSLY true — dragging the dependent "other
  // person" fields into `required` while the user has selected nothing. Mirrors
  // the component's evaluateRequiredIfAllVisibleDirectorsMatch guard. This guard
  // became load-bearing on 2026-07-29 when the resolver above stopped falling
  // back to 2; without it, every count-less payload flips the other-person
  // fields to required.
  if (count <= ROLE5_NO_DIRECTOR_COUNT_SELECTED) {
    return false;
  }

  const limit = Math.min(count, config.directorFields.length);
  for (let directorIndex = 0; directorIndex < limit; directorIndex++) {
    const fieldName = config.directorFields[directorIndex];
    const value = sheetData[fieldName];
    if (!value || value !== config.equals) {
      return false;
    }
  }
  return true;
}

/**
 * True when a value counts as "the preparer put something here". Whitespace is
 * not a commitment, so a space-only entry reads as empty — matching how
 * `Validators.required` treats it.
 */
function role5HasValue(value: unknown): boolean {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

/**
 * THE required-state rule for the "other person primarily responsible for
 * financial management" trio — Name, Type of Identification, Identification
 * number. Imported by BOTH the component's `updateConditionalFieldsVisibility`
 * and the pure `role5StatementByDirectorsValidator`, so the editor's red
 * asterisk, the inline errors and the headless listing verdict cannot disagree.
 *
 * The trio is mandatory under EITHER condition (P.O. 2026-08-24):
 *
 *   A. every VISIBLE director reports `config.equals` ("Not primarily
 *      responsible for financial management of the company") — the original
 *      schema-declared `requiredIfAllVisibleDirectorsMatch` rule, unchanged; OR
 *   B. ANY ONE of the three fields carries a value. Filling one commits the
 *      preparer to the whole group, so the remaining two become mandatory.
 *
 * Condition B is what makes the group self-consistent under editing. Without
 * it, a preparer who typed an identification number and then cleared the paired
 * Type dropdown was left with a sheet that blocked submission while every
 * emptied field claimed to be optional.
 *
 * `groupFields` is derived from the schema by the caller (every control
 * carrying `requiredIfAllVisibleDirectorsMatch`), never hardcoded — the same
 * "schema-driven, no hardcoded qnames" contract the rest of this rule follows.
 */
export function evaluateRole5OtherPersonRequired(
  sheetData: Record<string, any>,
  config: {
    directorCountField: string;
    directorFields: ReadonlyArray<string>;
    equals: string;
  },
  groupFields: ReadonlyArray<string>
): boolean {
  if (evaluateRole5RequiredIfAllVisibleDirectorsMatch(sheetData, config)) {
    return true;
  }
  return groupFields.some((qname) => role5HasValue(sheetData[qname]));
}
